import { useEffect, useRef } from "react";
import { Button, VStack } from "@chakra-ui/react";
import { LuRuler } from "react-icons/lu";
import { Tooltip } from "../../ui/tooltip";
import { useAppStore } from "../../../context/store";
import Type from "../../../utilies/Types";
import { createMeasureDistanceTool } from "../../../utilies/other/MeasureDistance";
import { getCombinedKeysText } from "../../../utilies/Utility";

function MeasureDistance() {
  const measureDistanceMode = useAppStore((s) => s.measureDistanceMode);
  const viewerRef = useAppStore((s) => s.viewerRef);
  const savedHotkeysRef = useAppStore((s) => s.savedHotkeysRef);
  const setMeasureDistanceMode = useAppStore((s) => s.setMeasureDistanceMode);
  const toolRef = useRef(null);
  const setLineIntersectMeasureMode = useAppStore((s) => s.setLineIntersectMeasureMode);
  const setOverlayTextMode = useAppStore.getState().setOverlayTextMode;

  useEffect(() => {
    const isMod = (k) => ["shift", "ctrl", "alt", "meta"].includes(String(k).toLowerCase());
    const isAlpha = (k) => /^[a-z]$/i.test(String(k || ""));
    const normalizeKey = (k) => {
      let key = String(k || "").toLowerCase();
      if (key === "control") key = "ctrl";
      if (key === " ") key = "space";
      return key;
    };

    const getCombo = (type) => savedHotkeysRef.current.find((h) => h.type === type)?.combination || [];

    const matches = (e, combo) => {
      if (!combo || combo.length === 0) return false;

      const norm = combo.map(normalizeKey);

      const want = {
        shift: norm.includes("shift"),
        ctrl: norm.includes("ctrl"),
        alt: norm.includes("alt"),
        meta: norm.includes("meta"),
      };

      if (e.shiftKey !== want.shift) return false;
      if (e.ctrlKey !== want.ctrl) return false;
      if (e.altKey !== want.alt) return false;
      if (e.metaKey !== want.meta) return false;

      const main = norm.find((k) => !isMod(k));
      if (!main) return true;

      return normalizeKey(e.key) === main;
    };

    // -------------------------
    // 3-key sequential support
    // combo: [mod, letter1, letter2]
    // user: mod+letter1, then within 600ms press letter2 (with same mods or no mods)
    // -------------------------
    const PENDING_KEY = "__measure_distance_hotkey_pending__";
    if (!window[PENDING_KEY]) {
      window[PENDING_KEY] = {
        active: false,
        mods: [],
        stepKey: "",
        finalKey: "",
        expiresAt: 0,
        tid: null,
      };
    }
    const pending = window[PENDING_KEY];

    const resetPending = () => {
      if (pending.tid) clearTimeout(pending.tid);
      pending.active = false;
      pending.mods = [];
      pending.stepKey = "";
      pending.finalKey = "";
      pending.expiresAt = 0;
      pending.tid = null;
    };

    const modsMatchExactly = (e, mods) => {
      const needShift = mods.includes("shift");
      const needCtrl = mods.includes("ctrl");
      const needAlt = mods.includes("alt");
      const needMeta = mods.includes("meta");

      if (e.shiftKey !== needShift) return false;
      if (e.ctrlKey !== needCtrl) return false;
      if (e.altKey !== needAlt) return false;
      if (e.metaKey !== needMeta) return false;
      return true;
    };

    const modsAreNone = (e) => !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;

    const isSequential3Combo = (combo) => {
      if (!combo || combo.length !== 3) return false;

      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));

      return mods.length === 1 && nonMods.length === 2 && isAlpha(nonMods[0]) && isAlpha(nonMods[1]);
    };

    const getSequentialParts = (combo) => {
      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));
      return { mods, stepKey: nonMods[0], finalKey: nonMods[1] };
    };

    const startPending = (mods, stepKey, finalKey) => {
      resetPending();
      pending.active = true;
      pending.mods = mods;
      pending.stepKey = stepKey;
      pending.finalKey = finalKey;
      pending.expiresAt = Date.now() + 3000;
      pending.tid = setTimeout(() => resetPending(), 3000);
    };

    const handleKeyDown = (event) => {
      const combo = getCombo(Type.HOTKEYS.MEASURE_DISTANCE);
      const key = normalizeKey(event.key);

      // 1) pending final key check
      if (pending.active) {
        if (Date.now() > pending.expiresAt) {
          resetPending();
        } else {
          const okMods = modsMatchExactly(event, pending.mods) || modsAreNone(event);

          if (okMods && key === normalizeKey(pending.finalKey)) {
            event.preventDefault();
            resetPending();
            setMeasureDistanceMode((prev) => !prev);
            return;
          }

          if (!isMod(key)) resetPending();
        }
      }

      // 2) sequential start
      if (isSequential3Combo(combo)) {
        const { mods, stepKey, finalKey } = getSequentialParts(combo);

        if (modsMatchExactly(event, mods) && key === normalizeKey(stepKey)) {
          event.preventDefault();
          startPending(mods, stepKey, finalKey);
          return;
        }
      }

      // 3) normal one-shot
      if (matches(event, combo)) {
        event.preventDefault();
        resetPending();
        setMeasureDistanceMode((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      resetPending();
    };
  }, [savedHotkeysRef, setMeasureDistanceMode]);

  useEffect(() => {
    const viewer = viewerRef?.current;
    if (!viewer) return;

    if (measureDistanceMode) {
      toolRef.current?.destroy();
      toolRef.current = createMeasureDistanceTool(viewer, {
        onFinish: () => setMeasureDistanceMode(false),
      });
      setLineIntersectMeasureMode(false);
      setOverlayTextMode(false);
    } else {
      toolRef.current?.destroy();
      toolRef.current = null;
    }

    return () => {
      toolRef.current?.destroy();
      toolRef.current = null;
    };
  }, [measureDistanceMode]);

  return (
    <Button
      variant="solid"
      p={0}
      opacity={1}
      bg={measureDistanceMode ? "green" : "white"}
      color={measureDistanceMode ? "white" : "MenuText"}
      colorPalette={measureDistanceMode ? "green" : "white"}
      onClick={() => setMeasureDistanceMode((prev) => !prev)}
    >
      <VStack py={2} gap={1}>
        <Tooltip content={"Measure Distance " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.MEASURE_DISTANCE)}>
          <LuRuler size={20} />
        </Tooltip>
      </VStack>
    </Button>
  );
}

export default MeasureDistance;
